import React, { useState, useEffect, useMemo } from 'react';
import styles from './UnifiedSearchFilterBar.module.css';

/**
 * Componente reutilizable de barra de filtros y búsqueda
 * 
 * USO:
 * <UnifiedSearchFilterBar
 *   filterConfig={[
 *     { key: 'cicloEscolar', label: 'Ciclo Escolar', apiService: getCycles, idField: 'id', labelField: 'nombre_ciclo' },
 *     { key: 'campus', label: 'Campus', apiService: getCampus, idField: 'id_campus', labelField: 'nombre_campus' }
 *   ]}
 *   filters={filters}
 *   onFilterChange={onFilterChange}
 *   searchTerm={searchTerm}
 *   onSearch={onSearch}
 *   searchPlaceholder="Buscar por Matrícula o Nombre"
 * />
 * 
 * PROPS:
 * - filterConfig: Array de objetos que definen los filtros
 *   - key: Identificador del filtro (usado en filters object)
 *   - label: Etiqueta a mostrar
 *   - apiService: Función async que retorna array de opciones
 *   - idField: Campo a usar como value en option (default: 'id')
 *   - labelField: Campo a usar como display en option (default: 'nombre')
 *   - defaultOption: Texto del option por defecto (default: 'Todos')
 *   - setDefault: Si true, selecciona automáticamente la primera opción
 * - filters: Object con valores actuales de filtros { cicloEscolar: '123', campus: '456' }
 * - onFilterChange: Callback (key, value) cuando un filtro cambia
 * - searchTerm: String con término de búsqueda actual
 * - onSearch: Callback (term) cuando se ejecuta búsqueda
 * - searchPlaceholder: Placeholder para el input de búsqueda
 * - showSearchIcon: Si se muestra icono de búsqueda (default: true)
 * - searchButtonText: Texto del botón (default: 'Buscar')
 * - variant: 'standard' | 'compact' (default: 'standard')
 */
function UnifiedSearchFilterBar({
  filterConfig = [],
  filters = {},
  onFilterChange = () => {},
  searchTerm = '',
  onSearch = () => {},
  searchPlaceholder = 'Buscar',
  showSearchIcon = true,
  searchButtonText = 'Buscar',
  variant = 'standard'
}) {
  // Estado local para datos de dropdowns
  const [dropdownData, setDropdownData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Cargar datos de APIs especificadas en filterConfig
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const data = {};
        const promises = [];

        // Crear promesas para cada filtro que tiene apiService
        filterConfig.forEach(config => {
          if (config.apiService && typeof config.apiService === 'function') {
            promises.push(
              config.apiService()
                .then(result => {
                  data[config.key] = result || [];
                  return { key: config.key, data: result || [] };
                })
                .catch(err => {
                  console.error(`Error cargando ${config.key}:`, err);
                  data[config.key] = [];
                })
            );
          }
        });

        // Ejecutar todas las promesas en paralelo
        if (promises.length > 0) {
          await Promise.all(promises);
        }

        setDropdownData(data);

        // Establecer valores por defecto si está configurado
        filterConfig.forEach(config => {
          if (config.setDefault && data[config.key] && data[config.key].length > 0) {
            const firstItem = data[config.key][0];
            const idValue = firstItem[config.idField || 'id'];
            if (idValue && !filters[config.key]) {
              onFilterChange(config.key, idValue);
            }
          }
        });
      } catch (err) {
        console.error('Error en UnifiedSearchFilterBar:', err);
        setError('Error al cargar filtros');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [filterConfig, filters, onFilterChange]);

  // Ordenar ciclos si existen
  const sortedDropdownData = useMemo(() => {
    const sorted = { ...dropdownData };
    
    // Si hay cicloEscolar, ordenar por año y período descendente
    if (sorted.cicloEscolar && Array.isArray(sorted.cicloEscolar)) {
      sorted.cicloEscolar = [...sorted.cicloEscolar].sort((a, b) => {
        const getAnio = c => Number(c.anio || (c.nombre_ciclo ? c.nombre_ciclo.split('-')[0] : 0));
        const getPeriodo = c => Number(c.numero_periodo || (c.nombre_ciclo ? c.nombre_ciclo.split('-')[1] : 0));
        const anioA = getAnio(a);
        const anioB = getAnio(b);
        if (anioA !== anioB) return anioB - anioA;
        return getPeriodo(b) - getPeriodo(a);
      });
    }
    
    return sorted;
  }, [dropdownData]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      onSearch(searchTerm);
    }
  };

  const containerClass = `${styles.container} ${styles[variant]}`;

  return (
    <div className={containerClass}>
      <div className={styles.filtersWrapper}>
        {/* Renderizar selects para cada filtro */}
        {filterConfig.map((config) => (
          <div key={config.key} className={styles.filterGroup}>
            <label className={styles.filterLabel}>{config.label}</label>
            <select
              className={styles.filterSelect}
              value={filters[config.key] || ''}
              onChange={(e) => onFilterChange(config.key, e.target.value)}
              disabled={loading}
            >
              <option value="">
                {config.defaultOption || 'Todos'}
              </option>
              {sortedDropdownData[config.key] && sortedDropdownData[config.key].map((item) => {
                const idValue = item[config.idField || 'id'];
                const labelValue = item[config.labelField || 'nombre'];
                const displayValue = labelValue || `${item.anio}-${item.numero_periodo}`;
                
                return (
                  <option key={idValue} value={idValue}>
                    {displayValue}
                  </option>
                );
              })}
            </select>
          </div>
        ))}
      </div>

      {/* Search input + button */}
      <div className={styles.searchWrapper}>
        <div className={styles.searchInputContainer}>
          {showSearchIcon && (
            <svg className={styles.searchIcon} width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
          <input
            type="text"
            className={styles.searchInput}
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(e) => onSearch(e.target.value)}
            onKeyPress={handleKeyPress}
          />
        </div>
        <button
          className={styles.searchButton}
          onClick={() => onSearch(searchTerm)}
          disabled={loading}
        >
          {searchButtonText}
        </button>
      </div>

      {error && <div className={styles.error}>{error}</div>}
    </div>
  );
}

export default UnifiedSearchFilterBar;
